import ObexRequestsHandlerP80 from '../handlers/ObexRequestsHandlerP80';
import { getAccessToken, getActiveCompanyId, buildQuery, unwrapList, unwrapError, getTransportError } from './apiUtils';

export interface InvoiceSummary {
  id: string;
  number: string;
  clientId: string;
  status: string;
  total: number;
  issueDate: string;
  dueDate: string;
}

// Emitidas y sin cobrar
export const OPEN_INVOICE_STATUSES = ['issued', 'executive'];

// Emitidas, cobradas o no: las que admiten nota de crédito
export const CREDITABLE_INVOICE_STATUSES = ['issued', 'executive', 'collected', 'collected_negotiated'];

export interface InvoiceLinePayload {
  description: string;
  quantity: number;
  unitPrice: number;
  taxRate: number;
}

const requireToken = () => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');
  return token;
};

const unwrapResponse = (res: any) => {
  let out = res;
  if (out && out.data) out = out.data;
  if (out && out.result) out = out.result;
  return out ? out.response : null;
};

export const listInvoices = async (): Promise<InvoiceSummary[]> => {
  const token = requireToken();
  // Sin limit, invoices_list devuelve solo las 10 últimas
  const res = await ObexRequestsHandlerP80.get(`/invoices_list${buildQuery({ access_token: token, company_id: getActiveCompanyId(), limit: 500 })}`);
  const transportError = getTransportError(res);
  if (transportError) throw new Error(transportError);
  return unwrapList(res).map((raw: any) => ({
    id:        String(raw.id),
    number:    raw.invoice_number || '',
    clientId:  raw.client_id != null ? String(raw.client_id) : '',
    status:    raw.status || '',
    total:     parseFloat(raw.total) || 0,
    issueDate: raw.issue_date ? String(raw.issue_date).slice(0, 10) : '',
    dueDate:   raw.due_date ? String(raw.due_date).slice(0, 10) : ''
  }));
};

// Siguiente número de la serie (p. ej. F-2026-007) a partir de las facturas existentes
export const nextInvoiceNumber = (invoices: { number: string }[], prefix: string) => {
  const pattern = new RegExp(`^${prefix}(\\d+)$`);
  const max = invoices.reduce((acc, inv) => {
    const match = inv.number.match(pattern);
    return match ? Math.max(acc, parseInt(match[1], 10)) : acc;
  }, 0);
  return `${prefix}${String(max + 1).padStart(3, '0')}`;
};

export const createInvoice = async (payload: {
  clientId: string;
  number: string;
  subtotal: number;
  taxAmount: number;
  total: number;
}): Promise<string> => {
  const token = requireToken();
  const res = await ObexRequestsHandlerP80.get(`/invoices_create${buildQuery({
    access_token:   token,
    company_id:     getActiveCompanyId(),
    client_id:      payload.clientId,
    invoice_number: payload.number,
    subtotal:       payload.subtotal.toFixed(2),
    tax_amount:     payload.taxAmount.toFixed(2),
    total:          payload.total.toFixed(2),
    currency:       'EUR',
    status:         'draft'
  })}`);
  const created = unwrapResponse(res);
  const id = created && typeof created === 'object' ? (Array.isArray(created) ? created[0]?.id : created.id) : null;
  if (!id) throw new Error(unwrapError(res, 'El servidor no devolvió el identificador de la factura.'));
  return String(id);
};

export const addInvoiceLine = async (invoiceId: string, line: InvoiceLinePayload) => {
  const token = requireToken();
  const res = await ObexRequestsHandlerP80.get(`/invoice_lines_add${buildQuery({
    access_token: token,
    invoice_id:   invoiceId,
    description:  line.description,
    quantity:     line.quantity,
    unit_price:   line.unitPrice.toFixed(2),
    discount_pct: 0,
    tax_rate:     line.taxRate,
    line_total:   (line.quantity * line.unitPrice).toFixed(2)
  })}`);
  const transportError = getTransportError(res);
  if (transportError) throw new Error(transportError);
};

// invoices_update reescribe siempre serie y forma de pago: si no se mandan guardaría "None",
// así que la forma de pago va vacía a propósito (buildQuery omitiría el parámetro)
export const updateInvoiceTerms = async (invoiceId: string, terms: { series: string; dueDate: string }) => {
  const token = requireToken();
  const query = buildQuery({ access_token: token, id: invoiceId, series: terms.series, due_date: terms.dueDate });
  const res = await ObexRequestsHandlerP80.get(`/invoices_update${query}&payment_method=`);
  const transportError = getTransportError(res);
  if (transportError) throw new Error(transportError);
};

export const issueInvoice = async (invoiceId: string) => {
  const token = requireToken();
  const res = await ObexRequestsHandlerP80.get(`/invoices_issue${buildQuery({ access_token: token, id: invoiceId })}`);
  const transportError = getTransportError(res);
  if (transportError) throw new Error(transportError);
};

// Líneas de una factura: llegan dentro de invoices_get
export const getInvoiceLines = async (invoiceId: string): Promise<InvoiceLinePayload[]> => {
  const token = requireToken();
  const res = await ObexRequestsHandlerP80.get(`/invoices_get${buildQuery({ access_token: token, id: invoiceId })}`);
  const transportError = getTransportError(res);
  if (transportError) throw new Error(transportError);
  const detail = unwrapResponse(res);
  const lines = detail && Array.isArray(detail.lines) ? detail.lines : [];
  return lines.map((l: any) => ({
    description: l.description || '',
    quantity:    parseFloat(l.quantity) || 1,
    unitPrice:   parseFloat(l.unit_price) || 0,
    taxRate:     parseFloat(l.tax_rate) || 0
  }));
};

// Marca la factura como cobrada entera. Con cuenta bancaria, el backend crea además el movimiento de banco
export const collectInvoice = async (invoiceId: string, opts: { reference: string; bankAccountId: string }) => {
  const token = requireToken();
  const res = await ObexRequestsHandlerP80.get(`/invoices_collect${buildQuery({
    access_token:           token,
    id:                     invoiceId,
    // El backend mete la referencia tal cual en el SQL: una comilla lo rompería
    payment_instrument_ref: opts.reference.replace(/'/g, ''),
    bank_account_id:        opts.bankAccountId
  })}`);
  const transportError = getTransportError(res);
  if (transportError) throw new Error(transportError);
  const message = unwrapResponse(res);
  if (typeof message === 'string' && !/cobrada exitosamente/i.test(message)) throw new Error(message);
};

// Datos del cliente real que necesitan los documentos de venta
export interface BillingClient {
  id: string;
  name: string;
  cif: string;
  email: string;
  address: string;
  paymentTermsDays: number | null;
  // Cuenta 43x de la ficha del cliente, la que se carga al emitirle una factura
  accountCode: string;
}

export const listBillingClients = async (): Promise<BillingClient[]> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');

  const res = await ObexRequestsHandlerP80.get(`/client-list${buildQuery({ access_token: token, page: 1, page_size: 100 })}`);
  const transportError = getTransportError(res);
  if (transportError) throw new Error(transportError);
  return unwrapList(res).map((raw: any) => ({
    id:      String(raw.id),
    name:    raw.name || '',
    cif:     raw.cif || '',
    email:   raw.email || '',
    address: raw.address || '',
    paymentTermsDays: raw.payment_terms_days === null || raw.payment_terms_days === undefined
      ? null
      : Number(raw.payment_terms_days),
    accountCode: raw.account_code || '430000'
  }));
};
